/**
 * Home dashboard data, sourced from the Owners Hub Stats API.
 *
 * Wires the three "Key Metrics" sections — Acquisition, Financial Performance and
 * Money Flow — to `state.ownerHubStats`, returning the SAME `{ data, loading }`
 * shape per section that `useMockEndpoint` did, so the presentational sections are
 * untouched.
 *
 * All three sections read ONE data set (`daily_client_activity`). Per range the
 * dashboard makes TWO requests (shared across the sections):
 *  - a `data-set-history` over the selected window → charts + headline figures;
 *  - a SEPARATE `data-set-history` over the equal period immediately before it →
 *    the period-over-period delta baseline (the API has no comparison field).
 *
 * The current and prior windows are kept as SEPARATE calls (never one extended
 * window) on purpose: the backend only serves 5-minute-fresh data for the current
 * UTC day, so a query that reached back into the prior period would drop onto the
 * collapsed pre-calculation and lose the live updates. For the live "Today" range
 * the current call is re-dispatched every ~5 minutes (stable key → in-place
 * refresh). The comparison scales with the range (see `COMPARISON_LABEL`). When the
 * current request errors (no cached data), figures render `N/A`. See
 * `stats-mapping.ts` / `missing-data.md`.
 */
import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  fetchStatsDataSetHistory,
  selectStatsDataSetHistory,
  selectStatsDataSetHistoryError,
  selectStatsDataSetHistoryLoading,
  type StatsHistoryEntry,
  type StatsHistoryRef,
} from '@/store/slices/ownerHubStats';

import type { HomeData, HomePeriod, HomeStat } from '../../mock-data/types';
import { cohortLabels, HOME_STATS, homeWindow, intervalIncludesToday, shiftWindow, type HomeKpiSpec } from './stats-mapping';
import { type Bucket, chartSeries, dayPair, deltaPct, liveBuckets, matchedBaseline, resolvePeriods, sumBuckets, toBuckets } from './stats-shape';
import { LIVE_REFRESH_MS, useRefreshTick } from './useRefreshTick';

export type HomeSectionResult<T> = { data: T; loading: boolean };

export type HomeStatsResult = {
  acquisition: HomeSectionResult<HomeData['acquisition']>;
  financial: HomeSectionResult<HomeData['financial']>;
  moneyFlow: HomeSectionResult<HomeData['moneyFlow']>;
};

const DATA_SET = HOME_STATS.acquisition.dataSet; // all three sections share it

/** Build one figure: current-period total, delta vs the count-matched prior period. */
const buildStat = (current: Bucket[], baseline: Bucket[], spec: HomeKpiSpec, errored: boolean): HomeStat => {
  if (spec.missing) {
    // No API field — render `?` (see Player Balance). Value/delta are inert.
    return { value: 0, delta: 0, prefix: spec.prefix, suffix: spec.suffix, missing: true };
  }
  if (errored) {
    // The request is failing and nothing is cached: render `N/A` instead of a fake 0.
    return { value: 0, delta: 0, prefix: spec.prefix, suffix: spec.suffix, error: true };
  }
  const total = sumBuckets(current, spec.fields);
  return {
    value: total * (spec.scale ?? 1),
    delta: deltaPct(total, sumBuckets(baseline, spec.fields)),
    prefix: spec.prefix,
    suffix: spec.suffix,
  };
};

/**
 * Stale-while-revalidate loading: the skeleton shows ONLY when there is no cached
 * history to display. First paint / first load (no entry yet) shows the skeleton;
 * a background refresh over an existing entry (including the Today 5-min refresh)
 * keeps the cached figures on screen. A first load that fails stops loading (and
 * falls through to the `N/A` error state); a missing brand reads as loading.
 */
const sectionLoading = (
  hasBrand: boolean,
  loadingFlag: boolean,
  entry: StatsHistoryEntry | undefined,
  error: string | undefined,
): boolean => !hasBrand || (entry === undefined && (loadingFlag || error === undefined));

/**
 * @param casinoInstanceId Active brand id (`displayBrand?.id`); `undefined` keeps
 *        the sections in their loading state until a brand resolves.
 * @param interval The selected UI range (`TODAY` / `YESTERDAY` / `7D` / `30D` / `90D` / `6M` / `1Y`).
 */
export const useHomeStats = (casinoInstanceId: string | undefined, interval: HomePeriod): HomeStatsResult => {
  const dispatch = useAppDispatch();

  // "Today" alone drives the window STRATEGY below (a separate prior-period call so
  // its window stays minimal). But the live latest-day slice is re-aggregated ~every
  // 5 min, so EVERY range whose window includes today (all but Yesterday) re-polls on
  // that cadence — not just Today. The tick is also an intentional window dep: it
  // re-reads the clock so the window rolls over at UTC midnight (`Date.now()` is a
  // hidden time dep).
  const live = interval === 'TODAY';
  const includesToday = intervalIncludesToday(interval);
  const tick = useRefreshTick(includesToday, LIVE_REFRESH_MS);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const window = React.useMemo(() => homeWindow(interval, Date.now()), [interval, tick]);
  const priorWindow = React.useMemo(() => shiftWindow(window, window.spanDays), [window]);

  // The MAIN request. Today fetches just the current window (a SEPARATE prior call
  // keeps it inside the backend's fresh window); every other range fetches ONE
  // extended window that also covers the prior period (split client-side) — fewer
  // calls, and those ranges already read the collapsed path so nothing live is lost.
  const mainWindow = live
    ? { startDate: window.startDate, endDate: window.endDate, intervalSlice: window.intervalSlice }
    : { startDate: priorWindow.startDate, endDate: window.endDate, intervalSlice: window.intervalSlice };

  const mainRef: StatsHistoryRef | undefined = casinoInstanceId ? { casinoInstanceId, dataSet: DATA_SET, ...mainWindow } : undefined;
  const priorRef: StatsHistoryRef | undefined =
    casinoInstanceId && live
      ? { casinoInstanceId, dataSet: DATA_SET, startDate: priorWindow.startDate, endDate: priorWindow.endDate, intervalSlice: window.intervalSlice }
      : undefined;

  const mainEntry = useAppSelector((s) => selectStatsDataSetHistory(s, mainRef));
  const mainLoading = useAppSelector((s) => selectStatsDataSetHistoryLoading(s, mainRef));
  const mainError = useAppSelector((s) => selectStatsDataSetHistoryError(s, mainRef));
  const priorEntry = useAppSelector((s) => selectStatsDataSetHistory(s, priorRef));

  // Main call — re-dispatched on the tick so Today refreshes in place.
  React.useEffect(() => {
    if (!casinoInstanceId) return;
    dispatch(
      fetchStatsDataSetHistory({
        casinoInstanceId,
        dataSet: DATA_SET,
        startDate: mainWindow.startDate,
        endDate: mainWindow.endDate,
        intervalSlice: mainWindow.intervalSlice,
      }),
    );
  }, [dispatch, casinoInstanceId, mainWindow.startDate, mainWindow.endDate, mainWindow.intervalSlice, tick]);

  // Separate prior call — Today only (other ranges get the prior period from the
  // extended main call above).
  React.useEffect(() => {
    if (!casinoInstanceId || !live) return;
    dispatch(
      fetchStatsDataSetHistory({
        casinoInstanceId,
        dataSet: DATA_SET,
        startDate: priorWindow.startDate,
        endDate: priorWindow.endDate,
        intervalSlice: window.intervalSlice,
      }),
    );
  }, [dispatch, casinoInstanceId, live, priorWindow.startDate, priorWindow.endDate, window.intervalSlice]);

  const hasBrand = Boolean(casinoInstanceId);
  // Drop future buckets the live range may report (a no-op for past ranges), then
  // resolve the current + prior periods (separate call for Today, split otherwise).
  const mainBuckets = liveBuckets(mainEntry ? toBuckets(mainEntry.data) : [], Date.now());
  const priorBuckets = priorEntry ? toBuckets(priorEntry.data) : [];
  const { current, previous } = resolvePeriods(mainBuckets, priorBuckets, live, Date.parse(window.startDate));
  const baseline = matchedBaseline(current, previous);

  const labels = cohortLabels(interval, current.map((b) => b.date));
  const loading = sectionLoading(hasBrand, mainLoading, mainEntry, mainError);
  // Hard error: the request failed and nothing is cached (so not loading): render `N/A`.
  const errored = !loading && mainError !== undefined && mainEntry === undefined;

  const acquisition: HomeData['acquisition'] = {
    registrations: buildStat(current, baseline, HOME_STATS.acquisition.kpis.registrations, errored),
    ftds: buildStat(current, baseline, HOME_STATS.acquisition.kpis.ftds, errored),
    xLabels: labels.xLabels,
    pointLabels: labels.pointLabels,
    bucketStarts: current.map((b) => Date.parse(b.date)),
    chart: {
      registrations: chartSeries(current, HOME_STATS.acquisition.series.registrations, HOME_STATS.acquisition.chartScale),
      ftds: chartSeries(current, HOME_STATS.acquisition.series.ftds, HOME_STATS.acquisition.chartScale),
    },
    days: {
      registrations: dayPair(current, previous, HOME_STATS.acquisition.series.registrations, HOME_STATS.acquisition.chartScale),
      ftds: dayPair(current, previous, HOME_STATS.acquisition.series.ftds, HOME_STATS.acquisition.chartScale),
    },
  };

  const financial: HomeData['financial'] = {
    grossRevenue: buildStat(current, baseline, HOME_STATS.financial.kpis.grossRevenue, errored),
    stakes: buildStat(current, baseline, HOME_STATS.financial.kpis.stakes, errored),
    wins: buildStat(current, baseline, HOME_STATS.financial.kpis.wins, errored),
    xLabels: labels.xLabels,
    pointLabels: labels.pointLabels,
    chart: {
      ggr: chartSeries(current, HOME_STATS.financial.series.ggr, HOME_STATS.financial.chartScale),
      stakes: chartSeries(current, HOME_STATS.financial.series.stakes, HOME_STATS.financial.chartScale),
      wins: chartSeries(current, HOME_STATS.financial.series.wins, HOME_STATS.financial.chartScale),
    },
    days: {
      ggr: dayPair(current, previous, HOME_STATS.financial.series.ggr, HOME_STATS.financial.chartScale),
      stakes: dayPair(current, previous, HOME_STATS.financial.series.stakes, HOME_STATS.financial.chartScale),
      wins: dayPair(current, previous, HOME_STATS.financial.series.wins, HOME_STATS.financial.chartScale),
    },
  };

  const moneyFlow: HomeData['moneyFlow'] = {
    deposits: buildStat(current, baseline, HOME_STATS.moneyFlow.kpis.deposits, errored),
    withdrawals: buildStat(current, baseline, HOME_STATS.moneyFlow.kpis.withdrawals, errored),
    playerBalance: buildStat(current, baseline, HOME_STATS.moneyFlow.kpis.playerBalance, errored),
  };

  return {
    acquisition: { data: acquisition, loading },
    financial: { data: financial, loading },
    moneyFlow: { data: moneyFlow, loading },
  };
};

export default useHomeStats;
